import { db } from '../../firebase';
import { collection, getDocs, getDoc, addDoc, updateDoc, deleteDoc, doc, query, orderBy, limit, where } from 'firebase/firestore';

export const getFranchiseAdminsCount = async () => {
    try {
        const snapshot = await getDocs(collection(db, 'franchiseAdmins'));
        return snapshot.size;
    } catch (error) {
        console.error('Error fetching franchise admins count:', error);
        return 0; // fallback to 0
    }
};

export const createFranchiseAdmin = async (data) => {
    try {
        // Generate a referral code: YATH + Year + Serial
        const q = query(collection(db, 'franchiseAdmins'), orderBy('createdAt', 'desc'), limit(1));
        const snapshot = await getDocs(q);
        
        let nextSerial = 1;
        if (!snapshot.empty) {
            const lastAdmin = snapshot.docs[0].data();
            if (lastAdmin.referralCode) {
                // Extracts the last digits from referral code like YATH2026001
                const lastSerialStr = lastAdmin.referralCode.replace(/[^0-9]/g, '').slice(-3); 
                const lastSerial = parseInt(lastSerialStr, 10);
                if (!isNaN(lastSerial)) {
                    nextSerial = lastSerial + 1;
                } else {
                    nextSerial = snapshot.size + 1; // Fallback
                }
            }
        }

        const year = new Date().getFullYear();
        const serial = String(nextSerial).padStart(3, '0');
        const referralCode = `YATH${year}${serial}`;

        const docRef = await addDoc(collection(db, 'franchiseAdmins'), {
            ...data,
            referralCode,
            status: 'active', // default status
            createdAt: new Date().toISOString()
        });
        return docRef.id;
    } catch (error) {
        console.error('Error creating franchise admin:', error);
        throw error;
    }
};

export const getFranchiseAdmins = async () => {
    try {
        const snapshot = await getDocs(collection(db, 'franchiseAdmins'));
        const admins = [];
        snapshot.forEach((doc) => {
            admins.push({ id: doc.id, ...doc.data() });
        });
        // Sort by createdAt descending
        return admins.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } catch (error) {
        console.error('Error fetching franchise admins:', error);
        return [];
    }
};

export const updateFranchiseAdminStatus = async (adminId, status) => {
    try {
        const adminRef = doc(db, 'franchiseAdmins', adminId);
        await updateDoc(adminRef, { status });
        return true;
    } catch (error) {
        console.error('Error updating franchise admin status:', error);
        throw error;
    }
};

export const deleteFranchiseAdmin = async (adminId) => {
    try {
        const adminRef = doc(db, 'franchiseAdmins', adminId);
        await deleteDoc(adminRef);
        return true;
    } catch (error) {
        console.error('Error deleting franchise admin:', error);
        throw error;
    }
};

export const verifyReferralCode = async (code) => {
    try {
        if (!code) return { isValid: false };
        const q = query(collection(db, 'franchiseAdmins'), where('referralCode', '==', code.toUpperCase()));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            const adminDoc = snapshot.docs[0];
            return { isValid: true, adminId: adminDoc.id, data: adminDoc.data() };
        }
        return { isValid: false, error: 'Invalid referral code' };
    } catch (error) {
        console.error('Error verifying referral code:', error);
        return { isValid: false, error: 'Error verifying code' };
    }
};

export const checkIfUserAlreadyReferred = async (userId, email, phone) => {
    try {
        // 1. Check by userId
        if (userId) {
            const userRef = doc(db, 'users', userId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
                const userData = userSnap.data();
                if (userData.referredBy || userData.franchiseReferralCode) {
                    return true;
                }
            }
        }
        
        // 2. Check by email
        if (email) {
            const qEmail = query(collection(db, 'users'), where('email', '==', email.trim()));
            const snapEmail = await getDocs(qEmail);
            for (const userDoc of snapEmail.docs) {
                const data = userDoc.data();
                if (data.referredBy || data.franchiseReferralCode) {
                    return true;
                }
            }
        }
        
        // 3. Check by phone
        if (phone) {
            const qPhone = query(collection(db, 'users'), where('phone', '==', phone.trim()));
            const snapPhone = await getDocs(qPhone);
            for (const userDoc of snapPhone.docs) {
                const data = userDoc.data();
                if (data.referredBy || data.franchiseReferralCode) {
                    return true;
                }
            }
        }

        // 4. Check if this email is already recorded in any franchise admin's recent activity
        if (email) {
            const adminsSnap = await getDocs(collection(db, 'franchiseAdmins'));
            let foundInActivity = false;
            adminsSnap.forEach((adminDoc) => {
                const data = adminDoc.data();
                const activity = data.recentActivity || [];
                const matched = activity.some(act => 
                    act.email && act.email.toLowerCase() === email.trim().toLowerCase()
                );
                if (matched) {
                    foundInActivity = true;
                }
            });
            if (foundInActivity) {
                return true;
            }
        }
        
        return false;
    } catch (error) {
        console.error('Error checking if user already referred:', error);
        return false;
    }
};

export const recordReferral = async (adminId, userName, userEmail, dateStr, status = 'Started') => {
    try {
        if (!userName || !userEmail) return; // Need details to record activity

        const adminRef = doc(db, 'franchiseAdmins', adminId);
        const adminSnap = await getDoc(adminRef);
        if (adminSnap.exists()) {
            const adminData = adminSnap.data();
            const currentTotal = adminData.totalReferrals || 0;
            const currentActivity = adminData.recentActivity || [];
            
            const activityIndex = currentActivity.findIndex(activity => activity.email === userEmail);
            
            if (activityIndex !== -1) {
                // Update existing activity
                if (currentActivity[activityIndex].status !== status) {
                    const oldStatus = currentActivity[activityIndex].status;
                    currentActivity[activityIndex].status = status;
                    currentActivity[activityIndex].date = dateStr;
                    
                    let newTotal = currentTotal;
                    if (status === 'Completed' && oldStatus !== 'Completed') {
                        newTotal += 1;
                    }
                    
                    await updateDoc(adminRef, {
                        totalReferrals: newTotal,
                        recentActivity: currentActivity
                    });
                }
            } else {
                // New activity
                const newActivity = {
                    type: 'Referral',
                    name: userName,
                    email: userEmail,
                    date: dateStr,
                    status: status
                };
                
                let newTotal = currentTotal;
                if (status === 'Completed') newTotal += 1;

                await updateDoc(adminRef, {
                    totalReferrals: newTotal,
                    recentActivity: [newActivity, ...currentActivity]
                });
            }
        }
    } catch (error) {
        console.error('Error recording referral:', error);
    }
};

export const updateUserReferral = async (userId, adminId, adminName, referralCode) => {
    try {
        if (!userId) return;
        const userRef = doc(db, 'users', userId);
        
        // Only update if document exists
        const snap = await getDoc(userRef);
        if (snap.exists()) {
            await updateDoc(userRef, {
                referredBy: adminId,
                referredByName: adminName,
                franchiseReferralCode: referralCode
            });
            
            // Update local storage so useAuth() gets it instantly
            const userStr = localStorage.getItem('yathartha_auth');
            if (userStr) {
                const userObj = JSON.parse(userStr);
                userObj.referredBy = adminId;
                userObj.referredByName = adminName;
                userObj.franchiseReferralCode = referralCode;
                localStorage.setItem('yathartha_auth', JSON.stringify(userObj));
            }
        }
    } catch (error) {
        console.error('Error updating user referral:', error);
    }
};
